"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { fadeInUp, staggerContainer } from "@/lib/animations";
import { ChevronLeft, ChevronRight, X, ZoomIn } from "lucide-react";
import { useInView } from "react-intersection-observer";
import Image from "next/image";

const workspaces = [
  {
    id: 1,
    title: "Signature Entrance",
    description: "Id Works Hub gateway at KAFD",
    image: "/images/image1.jpg",
  },
  {
    id: 2,
    title: "Community Seating",
    description: "A modern hub for designers, makers, and innovators",
    image: "/images/image2.jpg",
  },
  {
    id: 3,
    title: "Social Lounge",
    description: "Flexible spaces for casual meetings and collaboration",
    image: "/images/image3.jpg",
  },
  {
    id: 4,
    title: "Creative Cafe Bar",
    description: "Where ideas start over coffee",
    image: "/images/image4.jpg",
  },
  {
    id: 5,
    title: "Design Library",
    description: "Quiet corner for focus, research, and inspiration",
    image: "/images/image5.jpg",
  },
];

export default function WorkspacesGallery() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<
    (typeof workspaces)[0] | null
  >(null);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % workspaces.length);
  };

  const prevSlide = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + workspaces.length) % workspaces.length
    );
  };

  // Always get 3 workspaces for consistent SSR
  const getVisibleWorkspaces = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(workspaces[(currentIndex + i) % workspaces.length]);
    }
    return visible;
  };

  const openLightbox = (workspace: (typeof workspaces)[0]) => {
    setSelectedImage(workspace);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = "auto";
  };

  return (
    <>
      <section
        ref={ref}
        id="workspaces"
        className="py-16 sm:py-24 lg:py-32 px-4 sm:px-6 lg:px-8 bg-card/40 overflow-hidden"
      >
        <div className="container mx-auto max-w-7xl">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Section Header */}
            <motion.div
              variants={fadeInUp}
              className="text-center mb-12 sm:mb-16 lg:mb-20"
            >
              <h2 className="text-heading text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 px-4">
                Our Workspaces
              </h2>
              <p className="text-text text-base sm:text-lg md:text-xl max-w-3xl mx-auto px-4 leading-relaxed">
                Explore our thoughtfully designed spaces crafted to inspire
                creativity and productivity
              </p>
            </motion.div>

            {/* Gallery */}
            <div className="relative overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12">
                {getVisibleWorkspaces().map((workspace, index) => (
                  <motion.div
                    key={workspace.id}
                    initial={{ opacity: 0, x: 100 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`group relative h-72 sm:h-80 lg:h-96 rounded-2xl sm:rounded-3xl overflow-hidden cursor-pointer ${
                      index === 0
                        ? ""
                        : index === 1
                        ? "hidden md:block"
                        : "hidden lg:block"
                    }`}
                    whileHover={{ y: -8 }}
                    onClick={() => openLightbox(workspace)}
                  >
                    {/* Image using Next.js Image component */}
                    <Image
                      src={workspace.image}
                      alt={workspace.title}
                      fill
                      className="object-cover transition-transform duration-700 group-hover:scale-110"
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10" />

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8 text-white z-20 transform transition-all duration-300 group-hover:translate-y-0 translate-y-2">
                      <h3 className="text-xl sm:text-2xl font-bold mb-1.5 sm:mb-2 drop-shadow-lg text-white">
                        {workspace.title}
                      </h3>
                      <p className="opacity-90 drop-shadow-md mb-3 sm:mb-4 text-gray-200 text-sm sm:text-base leading-relaxed">
                        {workspace.description}
                      </p>

                      {/* Zoom Icon */}
                      <div className="flex items-center gap-2 text-xs sm:text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span>Click to view full screen</span>
                      </div>
                    </div>

                    {/* Shine Effect on Hover */}
                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none z-30">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className="flex justify-center gap-3 sm:gap-4">
                <motion.button
                  onClick={prevSlide}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-card/80 backdrop-blur-xl border border-border flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Previous workspace"
                >
                  <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                </motion.button>
                <motion.button
                  onClick={nextSlide}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-card/80 backdrop-blur-xl border border-border flex items-center justify-center text-primary hover:bg-primary hover:text-white transition-all duration-300 shadow-lg"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Next workspace"
                >
                  <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
                </motion.button>
              </div>

              {/* Indicators */}
              <div className="flex justify-center gap-1.5 sm:gap-2 mt-6 sm:mt-8">
                {workspaces.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`h-1.5 sm:h-2 rounded-full transition-all duration-300 ${
                      index === currentIndex
                        ? "w-6 sm:w-8 bg-primary"
                        : "w-1.5 sm:w-2 bg-primary/30 hover:bg-primary/50"
                    }`}
                    aria-label={`Go to workspace ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-2 sm:p-4 md:p-6 lg:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
          >
            {/* Close Button */}
            <motion.button
              className="absolute top-4 right-4 sm:top-6 sm:right-6 md:top-8 md:right-8 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all z-50"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              onClick={closeLightbox}
              aria-label="Close lightbox"
            >
              <X className="w-5 h-5 sm:w-6 sm:h-6" />
            </motion.button>

            {/* Image Container */}
            <motion.div
              className="relative w-full h-full max-w-7xl max-h-[90vh] mx-auto flex flex-col"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative flex-1 w-full rounded-xl sm:rounded-2xl overflow-hidden">
                <Image
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  fill
                  className="object-contain"
                  sizes="90vw"
                  priority
                />
              </div>

              {/* Image Info */}
              <motion.div
                className="mt-4 sm:mt-6 text-center text-white px-4 pb-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold mb-2 line-clamp-2">
                  {selectedImage.title}
                </h3>
                <p className="text-xs sm:text-sm md:text-base lg:text-lg text-white/80 line-clamp-2">
                  {selectedImage.description}
                </p>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
